import React, { useEffect, useRef, createElement } from 'react';
import * as THREE from 'three';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';
import { CSS3DRenderer, CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
export function Game() {
  const containerRef = useRef<HTMLDivElement>(null);
  const hudRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x87ceeb);
    scene.fog = new THREE.Fog(0x87ceeb, 0, 60);
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(0, 1.6, 0);
    const renderer = new THREE.WebGLRenderer({
      antialias: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    const cssRenderer = new CSS3DRenderer();
    cssRenderer.setSize(window.innerWidth, window.innerHeight);
    cssRenderer.domElement.style.position = 'absolute';
    cssRenderer.domElement.style.top = '0';
    cssRenderer.domElement.style.pointerEvents = 'none';
    containerRef.current.appendChild(cssRenderer.domElement);
    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambientLight);
    const light1 = new THREE.PointLight(0xfff5e0, 1.2, 30);
    light1.position.set(0, 4.2, 0);
    scene.add(light1);
    const light2 = new THREE.PointLight(0xfff5e0, 1.2, 30);
    light2.position.set(0, 4.2, -10);
    scene.add(light2);
    const light3 = new THREE.PointLight(0xfff5e0, 1.2, 30);
    light3.position.set(0, 4.2, -20);
    scene.add(light3);
    // Track light states
    const lightsOn = [true, true, true];
    // Create ceiling lamp model
    function createLamp(centerZ: number): {
      group: THREE.Group;
      bulbMat: THREE.MeshStandardMaterial;
    } {
      const group = new THREE.Group();
      // Ceiling mount plate
      const mountPlate = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.05, 16), new THREE.MeshStandardMaterial({
        color: 0x444444,
        metalness: 0.8,
        roughness: 0.2
      }));
      mountPlate.position.set(0, 0, 0);
      group.add(mountPlate);
      // Rod hanging down
      const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.6, 8), new THREE.MeshStandardMaterial({
        color: 0x555555,
        metalness: 0.9,
        roughness: 0.1
      }));
      rod.position.set(0, -0.3, 0);
      group.add(rod);
      // Lamp shade (cone/frustum)
      const shade = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.5, 0.3, 16, 1, true), new THREE.MeshStandardMaterial({
        color: 0x333333,
        metalness: 0.6,
        roughness: 0.3,
        side: THREE.DoubleSide
      }));
      shade.position.set(0, -0.7, 0);
      group.add(shade);
      // Light bulb (glowing sphere)
      const bulbMat = new THREE.MeshStandardMaterial({
        color: 0xffffcc,
        emissive: 0xffee88,
        emissiveIntensity: 1.0
      });
      const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.12, 16, 16), bulbMat);
      bulb.position.set(0, -0.75, 0);
      group.add(bulb);
      group.position.set(0, 5, centerZ);
      scene.add(group);
      return {
        group,
        bulbMat
      };
    }
    const lamp1 = createLamp(0);
    const lamp2 = createLamp(-10);
    const lamp3 = createLamp(-20);
    // Toggle light function
    function toggleLight(index: number) {
      lightsOn[index] = !lightsOn[index];
      const on = lightsOn[index];
      const lights = [light1, light2, light3];
      const lamps = [lamp1, lamp2, lamp3];
      lights[index].intensity = on ? 1.2 : 0;
      lamps[index].bulbMat.emissive.setHex(on ? 0xffee88 : 0x000000);
      lamps[index].bulbMat.emissiveIntensity = on ? 1.0 : 0;
      lamps[index].bulbMat.color.setHex(on ? 0xffffcc : 0x333333);
    }
    // Room dimensions
    const roomWidth = 10;
    const roomHeight = 5;
    const roomDepth = 10;
    const doorWidth = 3;
    const doorHeight = 3.5;
    // Rooms layout (all along Z axis, going negative):
    // Room 1: z from +5 to -5 (center 0)
    // Room 2: z from -5 to -15 (center -10)
    // Room 3: z from -15 to -25 (center -20)
    // Shared wall Room1/Room2 at z = -5 (has door)
    // Shared wall Room2/Room3 at z = -15 (has door)
    const room1Center = 0;
    const room2Center = -10;
    const room3Center = -20;
    // Marble floor texture
    function createMarbleTexture(): THREE.CanvasTexture {
      const size = 512;
      const c = document.createElement('canvas');
      c.width = size;
      c.height = size;
      const ctx = c.getContext('2d')!;
      ctx.fillStyle = '#e8e0d8';
      ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < 40; i++) {
        ctx.beginPath();
        ctx.strokeStyle = `rgba(180, 170, 160, ${Math.random() * 0.3 + 0.1})`;
        ctx.lineWidth = Math.random() * 2 + 0.5;
        const startX = Math.random() * size;
        const startY = Math.random() * size;
        ctx.moveTo(startX, startY);
        for (let j = 0; j < 5; j++) {
          ctx.lineTo(startX + (Math.random() - 0.5) * 200, startY + (Math.random() - 0.5) * 200);
        }
        ctx.stroke();
      }
      const tileSize = size / 4;
      ctx.strokeStyle = 'rgba(160, 150, 140, 0.5)';
      ctx.lineWidth = 2;
      for (let x = 0; x <= size; x += tileSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, size);
        ctx.stroke();
      }
      for (let y = 0; y <= size; y += tileSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(size, y);
        ctx.stroke();
      }
      const texture = new THREE.CanvasTexture(c);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(2, 2);
      return texture;
    }
    const floorMaterial = new THREE.MeshStandardMaterial({
      map: createMarbleTexture(),
      roughness: 0.3,
      metalness: 0.1
    });
    const ceilingMaterial = new THREE.MeshStandardMaterial({
      color: 0x999999
    });
    // Wall materials - 3 shades of light blue
    const room1WallMat = new THREE.MeshStandardMaterial({
      color: 0xb0d4f1,
      side: THREE.DoubleSide
    });
    const room2WallMat = new THREE.MeshStandardMaterial({
      color: 0x8bbde0,
      side: THREE.DoubleSide
    });
    const room3WallMat = new THREE.MeshStandardMaterial({
      color: 0x6ba3cc,
      side: THREE.DoubleSide
    });
    // Helper: create a wall with a door opening (3 pieces: left, right, top)
    function addDoorWall(x: number, y: number, z: number, rotY: number, material: THREE.Material) {
      const sideWidth = (roomWidth - doorWidth) / 2;
      const left = new THREE.Mesh(new THREE.PlaneGeometry(sideWidth, roomHeight), material);
      left.position.set(x - roomWidth / 2 + sideWidth / 2, y, z);
      left.rotation.y = rotY;
      scene.add(left);
      const right = new THREE.Mesh(new THREE.PlaneGeometry(sideWidth, roomHeight), material);
      right.position.set(x + roomWidth / 2 - sideWidth / 2, y, z);
      right.rotation.y = rotY;
      scene.add(right);
      const top = new THREE.Mesh(new THREE.PlaneGeometry(doorWidth, roomHeight - doorHeight), material);
      top.position.set(x, y + doorHeight / 2 + (roomHeight - doorHeight) / 2, z);
      top.rotation.y = rotY;
      scene.add(top);
    }
    // Helper: create a solid wall
    function addSolidWall(x: number, y: number, z: number, w: number, h: number, rotY: number, material: THREE.Material) {
      const wall = new THREE.Mesh(new THREE.PlaneGeometry(w, h), material);
      wall.position.set(x, y, z);
      wall.rotation.y = rotY;
      scene.add(wall);
    }
    // Helper: add floor + ceiling for a room
    function addFloorCeiling(centerZ: number) {
      const floor = new THREE.Mesh(new THREE.PlaneGeometry(roomWidth, roomDepth), floorMaterial.clone());
      floor.rotation.x = -Math.PI / 2;
      floor.position.set(0, 0, centerZ);
      scene.add(floor);
      const ceil = new THREE.Mesh(new THREE.PlaneGeometry(roomWidth, roomDepth), ceilingMaterial);
      ceil.rotation.x = Math.PI / 2;
      ceil.position.set(0, roomHeight, centerZ);
      scene.add(ceil);
    }
    const halfW = roomWidth / 2;
    const halfD = roomDepth / 2;
    const wallY = roomHeight / 2;
    // ===== ROOM 1 (z: +5 to -5) - Lightest blue =====
    addFloorCeiling(room1Center);
    // Front wall (z=+5) - SOLID (no door, this is the far end)
    addSolidWall(0, wallY, room1Center + halfD, roomWidth, roomHeight, Math.PI, room1WallMat);
    // Left wall
    addSolidWall(-halfW, wallY, room1Center, roomDepth, roomHeight, Math.PI / 2, room1WallMat);
    // Right wall
    addSolidWall(halfW, wallY, room1Center, roomDepth, roomHeight, -Math.PI / 2, room1WallMat);
    // Back wall (z=-5) - DOOR to Room 2
    addDoorWall(0, wallY, room1Center - halfD, 0, room1WallMat);
    // Portrait in Room 1 - on the left wall
    const portraitFrame = new THREE.Group();
    const frameBorder = new THREE.Mesh(new THREE.BoxGeometry(2.2, 2.8, 0.1), new THREE.MeshStandardMaterial({
      color: 0x8b4513
    }));
    portraitFrame.add(frameBorder);
    const canvasEl = document.createElement('canvas');
    canvasEl.width = 512;
    canvasEl.height = 640;
    const ctx = canvasEl.getContext('2d')!;
    ctx.fillStyle = '#f5deb3';
    ctx.fillRect(0, 0, 512, 640);
    ctx.fillStyle = '#2a1a0a';
    ctx.font = 'bold 36px Georgia';
    ctx.textAlign = 'center';
    ctx.fillText('CV OF BEST', 256, 280);
    ctx.fillText('PERSON EVER', 256, 330);
    // Draw a simple portrait silhouette
    ctx.fillStyle = '#8b7355';
    ctx.beginPath();
    ctx.arc(256, 180, 60, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(206, 240, 100, 20);
    const textTexture = new THREE.CanvasTexture(canvasEl);
    const textMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2.5), new THREE.MeshBasicMaterial({
      map: textTexture
    }));
    textMesh.position.z = 0.06;
    portraitFrame.add(textMesh);
    portraitFrame.position.set(-halfW + 0.1, 2.5, 0);
    portraitFrame.rotation.y = Math.PI / 2;
    scene.add(portraitFrame);
    // Iframe in Room 1 - on the right wall
    const iframe = document.createElement('iframe');
    iframe.src = 'https://efirst.com.pl/';
    iframe.style.width = '1280px';
    iframe.style.height = '720px';
    iframe.style.border = 'none';
    iframe.style.backgroundColor = 'white';
    const cssObject = new CSS3DObject(iframe);
    cssObject.position.set(halfW - 0.05, 2.5, room1Center);
    cssObject.rotation.y = -Math.PI / 2;
    cssObject.scale.set(0.003, 0.003, 0.003);
    scene.add(cssObject);
    // Instruction sign - on left wall of Room 1, near the back door (z ~ -3)
    const signGroup = new THREE.Group();
    const signFrame = new THREE.Mesh(new THREE.BoxGeometry(2.5, 1.8, 0.08), new THREE.MeshStandardMaterial({
      color: 0x2a2a2a
    }));
    signGroup.add(signFrame);
    const signCanvas = document.createElement('canvas');
    signCanvas.width = 512;
    signCanvas.height = 360;
    const signCtx = signCanvas.getContext('2d')!;
    signCtx.fillStyle = '#1a1a2e';
    signCtx.fillRect(0, 0, 512, 360);
    // Border
    signCtx.strokeStyle = '#e0c080';
    signCtx.lineWidth = 4;
    signCtx.strokeRect(10, 10, 492, 340);
    // Title
    signCtx.fillStyle = '#e0c080';
    signCtx.font = 'bold 28px Georgia';
    signCtx.textAlign = 'center';
    signCtx.fillText('💡 LIGHT CONTROLS', 256, 60);
    // Instructions
    signCtx.font = '24px monospace';
    signCtx.fillStyle = '#ffffff';
    signCtx.textAlign = 'left';
    signCtx.fillText('  Press 1  →  Room 1 light', 40, 130);
    signCtx.fillText('  Press 2  →  Room 2 light', 40, 190);
    signCtx.fillText('  Press 3  →  Room 3 light', 40, 250);
    signCtx.font = '18px Georgia';
    signCtx.fillStyle = '#aaaaaa';
    signCtx.textAlign = 'center';
    signCtx.fillText('Toggle on / off', 256, 320);
    const signTexture = new THREE.CanvasTexture(signCanvas);
    const signMesh = new THREE.Mesh(new THREE.PlaneGeometry(2.3, 1.6), new THREE.MeshBasicMaterial({
      map: signTexture
    }));
    signMesh.position.z = 0.05;
    signGroup.add(signMesh);
    signGroup.position.set(-halfW + 0.1, 2.2, -3);
    signGroup.rotation.y = Math.PI / 2;
    scene.add(signGroup);
    // ===== ROOM 2 (z: -5 to -15) - Medium blue =====
    addFloorCeiling(room2Center);
    // Back wall (z=-5) - DOOR to Room 1 (shared wall)
    // Already covered by Room 1's back door wall - skip duplicate
    // Left wall
    addSolidWall(-halfW, wallY, room2Center, roomDepth, roomHeight, Math.PI / 2, room2WallMat);
    // Right wall
    addSolidWall(halfW, wallY, room2Center, roomDepth, roomHeight, -Math.PI / 2, room2WallMat);
    // Front wall (z=-15) - DOOR to Room 3
    addDoorWall(0, wallY, room2Center - halfD, Math.PI, room2WallMat);
    // ===== ROOM 3 (z: -15 to -25) - Darkest blue =====
    addFloorCeiling(room3Center);
    // Back wall (z=-15) - DOOR to Room 2 (shared wall)
    // Already covered by Room 2's front door wall - skip duplicate
    // Left wall
    addSolidWall(-halfW, wallY, room3Center, roomDepth, roomHeight, Math.PI / 2, room3WallMat);
    // Right wall
    addSolidWall(halfW, wallY, room3Center, roomDepth, roomHeight, -Math.PI / 2, room3WallMat);
    // Front wall (z=-25) - SOLID (dead end)
    addSolidWall(0, wallY, room3Center - halfD, roomWidth, roomHeight, Math.PI, room3WallMat);
    // Decorative box in Room 3
    const decorBox = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshStandardMaterial({
      color: 0xffaa00
    }));
    decorBox.position.set(0, 0.5, room3Center);
    scene.add(decorBox);
    // Controls
    const controls = new PointerLockControls(camera, renderer.domElement);
    const moveSpeed = 0.1;
    const velocity = new THREE.Vector3();
    const direction = new THREE.Vector3();
    const keys: {
      [key: string]: boolean;
    } = {};
    const onKeyDown = (e: KeyboardEvent) => {
      keys[e.code] = true;
      // Light toggle with 1, 2, 3
      if (e.code === 'Digit1') toggleLight(0);
      if (e.code === 'Digit2') toggleLight(1);
      if (e.code === 'Digit3') toggleLight(2);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      keys[e.code] = false;
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    renderer.domElement.addEventListener('click', () => {
      controls.lock();
    });
    controls.addEventListener('lock', () => {
      if (hudRef.current) hudRef.current.style.display = 'none';
    });
    controls.addEventListener('unlock', () => {
      if (hudRef.current) hudRef.current.style.display = 'block';
    });
    // Collision: 3 room boxes that share edges at doorways
    const margin = 0.3;
    const boundaries = [{
      minX: -halfW,
      maxX: halfW,
      minZ: room1Center - halfD,
      maxZ: room1Center + halfD
    }, {
      minX: -halfW,
      maxX: halfW,
      minZ: room2Center - halfD,
      maxZ: room2Center + halfD
    }, {
      minX: -halfW,
      maxX: halfW,
      minZ: room3Center - halfD,
      maxZ: room3Center + halfD
    }];
    function isInsideAnyRoom(x: number, z: number): boolean {
      for (const b of boundaries) {
        if (x > b.minX + margin && x < b.maxX - margin && z > b.minZ + margin && z < b.maxZ - margin) {
          return true;
        }
      }
      return false;
    }
    // Animation loop
    function animate() {
      requestAnimationFrame(animate);
      if (controls.isLocked) {
        direction.z = Number(keys['KeyW'] || false) - Number(keys['KeyS'] || false);
        direction.x = Number(keys['KeyD'] || false) - Number(keys['KeyA'] || false);
        direction.normalize();
        velocity.x = direction.x * moveSpeed;
        velocity.z = direction.z * moveSpeed;
        const forward = new THREE.Vector3();
        camera.getWorldDirection(forward);
        forward.y = 0;
        forward.normalize();
        const right = new THREE.Vector3();
        right.crossVectors(forward, camera.up);
        const moveVector = new THREE.Vector3();
        moveVector.addScaledVector(forward, velocity.z);
        moveVector.addScaledVector(right, velocity.x);
        const newX = camera.position.x + moveVector.x;
        const newZ = camera.position.z + moveVector.z;
        if (isInsideAnyRoom(newX, newZ)) {
          camera.position.x = newX;
          camera.position.z = newZ;
        }
      }
      renderer.render(scene, camera);
      cssRenderer.render(scene, camera);
    }
    animate();
    function handleResize() {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      cssRenderer.setSize(window.innerWidth, window.innerHeight);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      controls.dispose();
      renderer.dispose();
      if (containerRef.current) {
        containerRef.current.removeChild(renderer.domElement);
        containerRef.current.removeChild(cssRenderer.domElement);
      }
    };
  }, []);
  return <div ref={containerRef} className="relative w-full h-screen overflow-hidden bg-black" data-id="element-1">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10" data-id="element-2">
        <div className="w-6 h-0.5 bg-white/70 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-3"></div>
        <div className="w-0.5 h-6 bg-white/70 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-4"></div>
      </div>
      <div ref={hudRef} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white text-sm font-mono bg-black/50 px-4 py-2 rounded pointer-events-none z-10" data-id="element-5">
        WASD to move, Mouse to look, Click to start
      </div>
    </div>;
}